/* eslint-disable */
import React, { useState, useEffect } from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { 
  getSubnetsForReflector, 
  getSubnetsForCluster 
} from "../../request/request";
import { extractSubnets } from "../../../../helpers/formattersShapedData";

interface OutboundContentProps {
  values: any;
  setValues: (v: any) => void;
  reflectorId?: number; // Add reflectorId prop
  clusterId?: number; // Add clusterId prop
  getAccessTokenSilently?: () => Promise<string>; // Add token function prop
}

const OutboundContent: React.FC<OutboundContentProps> = ({ 
  values, 
  setValues, 
  reflectorId, 
  clusterId, 
  getAccessTokenSilently 
}) => {
  const [subnetToAdd, setSubnetToAdd] = useState("");
  const [subnetOptions, setSubnetOptions] = useState<string[]>([]);
  const [isLoadingSubnets, setIsLoadingSubnets] = useState(false);
  const subnets = values.subnets || [];

  // Fetch subnets from API
  useEffect(() => {
    const fetchSubnets = async () => {
      if (!getAccessTokenSilently || (!reflectorId && !clusterId)) {
        return;
      }

      setIsLoadingSubnets(true);
      try {
        const token = await getAccessTokenSilently();
        let response;

        if (clusterId) {
          // Fetch for cluster
          response = await getSubnetsForCluster(token, clusterId);
        } else if (reflectorId) {
          // Fetch for standalone reflector
          response = await getSubnetsForReflector(token, reflectorId);
        }

        if (response?.data?.data?.subnets) {
          // Extract subnets using centralized function
          const subnets = extractSubnets(response);
          setSubnetOptions(subnets);
        }
      } catch (error) {
        console.error('Error fetching subnets:', error);
        // Fallback to empty array on error
        setSubnetOptions([]);
      } finally {
        setIsLoadingSubnets(false);
      }
    };

    fetchSubnets();
  }, [reflectorId, clusterId, getAccessTokenSilently]);

  // Helper function to add subnet to array
  const addSubnet = (subnet: string) => {
    if (subnet && !subnets.includes(subnet)) {
      setValues({ 
        ...values, 
        subnets: [...subnets, subnet]
      });
      setSubnetToAdd(""); 
    }
  };

  // Helper function to remove subnet from array
  const removeSubnet = (subnet: string) => {
    setValues({ 
      ...values, 
      subnets: subnets.filter((s: string) => s !== subnet)
    });
  };

  return (
    <div className="flex flex-col gap-6 text-body">
      <div className="rounded-sm border border-semantic-border-secondary p-4 text-semantic-text-secondary text-body">
        Configure how subnets from cloud routing tables are advertised back to this BGP peer. Specify subnet prefixes, BGP communities, and next-hop preferences.
      </div>
      {/* Subnet configuration */}
      <div>
        <div className=" text-semantic-text-primary mb-2 text-body">Subnet configuration</div>
        <div className="flex gap-2 items-end w-full mb-2">
          <GenericSearchableDropdown
            items={subnetOptions.filter(opt => !subnets.includes(opt))}
            value={subnetToAdd}
            onSelect={setSubnetToAdd}
            defaultLabel="Select..."
            widthClass="w-full"
            className="flex-1"
            minWidthClass=""
            maxWidthClass=""
            disabled={isLoadingSubnets}
            emptyItemsMessage={isLoadingSubnets ? "Loading..." : "No subnets available"}
            allowCustomValues={true}
          />
          <GenericButton
            label="+"
            customHeight="35px"
            variant="secondary"
            shape="ghost"
            size="sm"
            onClick={() => addSubnet(subnetToAdd)}
            disabled={!subnetToAdd || subnets.includes(subnetToAdd) || isLoadingSubnets}
          />
        </div>
        {/* Display subnets as tags */}
        {subnets.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {subnets.map((subnet: string, index: number) => (
              <div key={index} className="flex items-center">
                <GenericTag
                  label={subnet}
                  variant="default"
                  removable={true}
                  onRemove={() => removeSubnet(subnet)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* BGP community */}
      <div>
        <GenericInput
          label="BGP community"
          value={values.bgpCommunity || ""}
          onChange={v => setValues({ ...values, bgpCommunity: v })}
          placeholder="E.g. 65535:65535"
          helperText="BGP community for advertised subnets"
          className="text-body"
        />
      </div>
      
      {/* Next-hop settings */}
      <div>
        <GenericInput
          label="Next-hop IP address"
          value={values.nexthopIPAddress || ""}
          onChange={v => setValues({ ...values, nexthopIPAddress: v })}
          placeholder="E.g. 192.168.1.1"
          helperText="IP address to use as next-hop in BGP advertisements (optional)"
          className="text-body"
        />
      </div>
    </div>
  );
};

export default OutboundContent;